/**
 * LibraryDrawer
 *
 * A side-drawer that shows the full Node Library (seeded RegistryNode records
 * plus all built-in node definitions).  Users can:
 *   - Browse / search by name or description
 *   - Filter by category
 *   - "Add to canvas" — fires onNodeAdd and closes the drawer
 *   - "Pin" — saves node type to localStorage so it appears in the palette
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AddCircleOutline as AddIcon,
  Close as CloseIcon,
  PushPin as PinIcon,
  PushPinOutlined as PinOutlinedIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { Node as ReactFlowNode } from 'reactflow';
import { enqueueSnackbar } from 'notistack';

import { registryService } from '../../services/registry.service';
import type { RegistryNode } from '../../types/registry.types';
import {
  ALL_BUILTIN_NODES,
  nodeTypeToReactFlowNode,
  type NodeType,
} from './NodePalette';

const PINNED_KEY = 'wg_pinned_nodes';

function getPinnedTypes(): string[] {
  try {
    return JSON.parse(localStorage.getItem(PINNED_KEY) || '[]');
  } catch {
    return [];
  }
}

function savePinnedTypes(types: string[]): void {
  localStorage.setItem(PINNED_KEY, JSON.stringify(types));
}

/** Map a RegistryNode from the API into a displayable LibraryEntry. */
interface LibraryEntry {
  id: string;
  node_type: string;
  display_name: string;
  description: string;
  category: string;
  tags: string[];
  install_count: number;
  is_official: boolean;
  status: string;
  /** Full NodeType definition if it's a built-in; undefined for npm-only nodes. */
  builtinDef?: NodeType;
}

function registryNodeToEntry(rn: RegistryNode, builtinDef?: NodeType): LibraryEntry {
  return {
    id: String(rn.id),
    node_type: rn.node_type,
    display_name: rn.display_name,
    description: rn.description || '',
    category: rn.category || 'Other',
    tags: rn.tags || [],
    install_count: rn.install_count ?? 0,
    is_official: rn.is_official ?? false,
    status: rn.status || 'working',
    builtinDef,
  };
}

const CATEGORY_ORDER = [
  'Triggers', 'Core', 'Actions', 'Communication', 'Productivity',
  'Project Management', 'CRM', 'ERP', 'Marketing', 'AI', 'AI Tools', 'Other',
];

function sortCategories(cats: string[]): string[] {
  return [...cats].sort((a, b) => {
    const ai = CATEGORY_ORDER.indexOf(a);
    const bi = CATEGORY_ORDER.indexOf(b);
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
}

interface LibraryDrawerProps {
  open: boolean;
  onClose: () => void;
  onNodeAdd: (node: ReactFlowNode) => void;
}

export const LibraryDrawer: React.FC<LibraryDrawerProps> = ({ open, onClose, onNodeAdd }) => {
  const [entries, setEntries] = useState<LibraryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [pinnedTypes, setPinnedTypes] = useState<string[]>(getPinnedTypes);

  // Build a lookup from node_type → built-in definition
  const builtinByType = useMemo(() => {
    const map = new Map<string, NodeType>();
    for (const n of ALL_BUILTIN_NODES) map.set(n.type, n);
    return map;
  }, []);

  const loadLibrary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await registryService.getNodes({ q: '', status: '' });
      const registryEntries = (resp.results || []).map((rn) =>
        registryNodeToEntry(rn, builtinByType.get(rn.node_type))
      );

      // Merge: start with registry (has install_count, official flag), then add
      // any built-in nodes not yet in the registry (so the drawer is always full).
      const registryTypes = new Set(registryEntries.map((e) => e.node_type));
      const localOnly: LibraryEntry[] = ALL_BUILTIN_NODES
        .filter((n) => !registryTypes.has(n.type))
        .map((n) => ({
          id: n.type,
          node_type: n.type,
          display_name: n.name,
          description: n.description,
          category: n.category,
          tags: [],
          install_count: 0,
          is_official: true,
          status: 'working',
          builtinDef: n,
        }));

      setEntries([...registryEntries, ...localOnly]);
    } catch (err: any) {
      setError('Failed to load library. Showing built-in nodes only.');
      // Fall back to showing all built-ins locally
      setEntries(
        ALL_BUILTIN_NODES.map((n) => ({
          id: n.type,
          node_type: n.type,
          display_name: n.name,
          description: n.description,
          category: n.category,
          tags: [],
          install_count: 0,
          is_official: true,
          status: 'working',
          builtinDef: n,
        }))
      );
    } finally {
      setLoading(false);
    }
  }, [builtinByType]);

  useEffect(() => {
    if (open) {
      loadLibrary();
      // Sync pinned state when drawer opens
      setPinnedTypes(getPinnedTypes());
    }
  }, [open, loadLibrary]);

  const isPinned = useCallback((nodeType: string) => pinnedTypes.includes(nodeType), [pinnedTypes]);

  const togglePin = useCallback((nodeType: string) => {
    const wasAlreadyPinned = pinnedTypes.includes(nodeType);
    setPinnedTypes((prev) => {
      const next = prev.includes(nodeType)
        ? prev.filter((t) => t !== nodeType)
        : [...prev, nodeType];
      savePinnedTypes(next);
      return next;
    });
    // Also track as installed so it appears in category sections of the palette
    if (!wasAlreadyPinned) {
      try {
        const existing: string[] = JSON.parse(localStorage.getItem('wg_installed_node_types') || '[]');
        if (!existing.includes(nodeType)) {
          existing.push(nodeType);
          localStorage.setItem('wg_installed_node_types', JSON.stringify(existing));
        }
      } catch {}
    }
    enqueueSnackbar(
      wasAlreadyPinned ? 'Removed from palette' : 'Added to palette',
      { variant: 'info', autoHideDuration: 1500 }
    );
  }, [pinnedTypes]);

  const handleAddToCanvas = useCallback(
    (entry: LibraryEntry) => {
      if (entry.builtinDef) {
        onNodeAdd(nodeTypeToReactFlowNode(entry.builtinDef));
      } else {
        // npm-imported node — add a generic action node placeholder
        onNodeAdd({
          id: `${entry.node_type}_${Date.now()}`,
          type: 'action',
          position: { x: Math.random() * 400, y: Math.random() * 400 },
          data: {
            label: entry.display_name,
            nodeType: entry.node_type,
            parameters: {},
            description: entry.description,
          },
        });
      }
      enqueueSnackbar(`Added "${entry.display_name}" to canvas`, { variant: 'success', autoHideDuration: 2000 });
      onClose();
    },
    [onNodeAdd, onClose]
  );

  // Filter logic
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return entries.filter((e) => {
      const matchesSearch = !q || `${e.display_name} ${e.node_type} ${e.description} ${e.tags.join(' ')}`.toLowerCase().includes(q);
      const matchesCategory = !categoryFilter || e.category === categoryFilter;
      return matchesSearch && matchesCategory;
    });
  }, [entries, search, categoryFilter]);

  const categories = useMemo(() => {
    const cats = new Set(entries.map((e) => e.category));
    return sortCategories(Array.from(cats));
  }, [entries]);

  const groupedFiltered = useMemo(() => {
    const groups: Record<string, LibraryEntry[]> = {};
    for (const e of filtered) {
      if (!groups[e.category]) groups[e.category] = [];
      groups[e.category].push(e);
    }
    return groups;
  }, [filtered]);

  const sortedGroupKeys = useMemo(
    () => sortCategories(Object.keys(groupedFiltered)),
    [groupedFiltered]
  );

  const statusColor = (status: string) => {
    if (status === 'working') return 'success';
    if (status === 'beta') return 'warning';
    return 'default';
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100vw', sm: 460 }, display: 'flex', flexDirection: 'column' } }}
    >
      {/* Header */}
      <Box sx={{ p: 2, pb: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h6" fontWeight="bold">Node Library</Typography>
            <Typography variant="body2" color="text.secondary">
              Browse and add nodes to your workflow
            </Typography>
          </Box>
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </Stack>

        {/* Search */}
        <TextField
          size="small"
          fullWidth
          placeholder="Search nodes..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mt: 1.5 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
          }}
        />

        {/* Category filter chips */}
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
          <Chip
            size="small"
            label="All"
            onClick={() => setCategoryFilter('')}
            color={categoryFilter === '' ? 'primary' : 'default'}
            variant={categoryFilter === '' ? 'filled' : 'outlined'}
          />
          {categories.map((cat) => (
            <Chip
              key={cat}
              size="small"
              label={cat}
              onClick={() => setCategoryFilter(cat === categoryFilter ? '' : cat)}
              color={categoryFilter === cat ? 'primary' : 'default'}
              variant={categoryFilter === cat ? 'filled' : 'outlined'}
            />
          ))}
        </Box>
      </Box>

      {/* Results count */}
      <Box sx={{ px: 2, py: 0.75, borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
        <Typography variant="caption" color="text.secondary">
          {filtered.length} node{filtered.length !== 1 ? 's' : ''}
          {search || categoryFilter ? ' matching filters' : ' available'}
        </Typography>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && error && (
          <Alert severity="warning" sx={{ m: 2 }}>{error}</Alert>
        )}

        {!loading && filtered.length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">No nodes match your search.</Typography>
          </Box>
        )}

        {!loading && sortedGroupKeys.map((category) => (
          <Box key={category}>
            {/* Category header */}
            <Box sx={{ px: 2, py: 1, bgcolor: 'grey.100', position: 'sticky', top: 0, zIndex: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="subtitle2" fontWeight="bold">{category}</Typography>
                <Chip size="small" label={groupedFiltered[category].length} />
              </Stack>
            </Box>

            {/* Node cards */}
            {groupedFiltered[category].map((entry) => (
              <Box key={entry.node_type}>
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Stack direction="row" alignItems="flex-start" spacing={1}>
                    {/* Info */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap">
                        <Typography variant="body2" fontWeight="bold" noWrap>
                          {entry.display_name}
                        </Typography>
                        {entry.is_official && (
                          <Chip size="small" label="Official" color="primary" variant="outlined" sx={{ height: 16, fontSize: '0.6rem' }} />
                        )}
                        <Chip
                          size="small"
                          label={entry.status}
                          color={statusColor(entry.status) as any}
                          variant="outlined"
                          sx={{ height: 16, fontSize: '0.6rem' }}
                        />
                      </Stack>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                        {entry.description}
                      </Typography>
                      {entry.tags.length > 0 && (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                          {entry.tags.slice(0, 4).map((tag) => (
                            <Typography key={tag} variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                              #{tag}
                            </Typography>
                          ))}
                        </Stack>
                      )}
                    </Box>

                    {/* Actions */}
                    <Stack direction="row" spacing={0.5} alignItems="center" flexShrink={0}>
                      <Tooltip title={isPinned(entry.node_type) ? 'Unpin from palette' : 'Pin to palette'}>
                        <IconButton
                          size="small"
                          onClick={() => togglePin(entry.node_type)}
                          color={isPinned(entry.node_type) ? 'primary' : 'default'}
                        >
                          {isPinned(entry.node_type) ? <PinIcon fontSize="small" /> : <PinOutlinedIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleAddToCanvas(entry)}
                        sx={{ whiteSpace: 'nowrap', minWidth: 90 }}
                      >
                        Add
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
                <Divider />
              </Box>
            ))}
          </Box>
        ))}
      </Box>

      {/* Footer tip */}
      <Box sx={{ p: 1.5, borderTop: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
        <Typography variant="caption" color="text.secondary">
          Tip: Pin nodes to keep them in the palette for quick access.
        </Typography>
      </Box>
    </Drawer>
  );
};
